import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import type { ApiAssignee } from './tasks';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

export interface ApiRunOfShowEntry {
  uuid: string;
  project_uuid: string;
  title: string;
  start_datetime: string;
  end_datetime: string;
  iana_timezone: string;
  location: string;
  assignees: ApiAssignee[];
  notes: string;
  created_by: ApiAssignee | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedRunOfShowEntriesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiRunOfShowEntry[];
}

export interface GetRunOfShowEntriesParams {
  projectUuid: string;
  page?: number;
  page_size?: number;
}

export const useGetRunOfShowEntries = (
  params: GetRunOfShowEntriesParams,
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedRunOfShowEntriesResponse>({
    queryKey: ['projects', params.projectUuid, 'run-of-show', params],
    enabled: options.enabled !== false && !!params.projectUuid,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedRunOfShowEntriesResponse>(
        `/projects/${params.projectUuid}/run-of-show-entries/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
  });
};

export interface RunOfShowEntryPayload {
  title: string;
  start_datetime: string;
  end_datetime: string;
  iana_timezone: string;
  location?: string;
  assignee_ids?: number[];
  notes?: string;
}

export const useCreateRunOfShowEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      payload,
    }: {
      projectUuid: string;
      payload: RunOfShowEntryPayload;
    }) => {
      const { data } = await apiClient.post<
        {
          success?: boolean;
          message?: string;
          data?: ApiRunOfShowEntry;
        } & ApiRunOfShowEntry
      >(`/projects/${projectUuid}/run-of-show-entries/`, payload);

      return data.data ?? data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Schedule entry created successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'run-of-show'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Unable to save schedule entry.'));
    },
  });
};

export const useUpdateRunOfShowEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      entryUuid,
      payload,
    }: {
      projectUuid: string;
      entryUuid: string;
      payload: Partial<RunOfShowEntryPayload>;
    }) => {
      const { data } = await apiClient.patch<
        {
          success?: boolean;
          message?: string;
          data?: ApiRunOfShowEntry;
        } & ApiRunOfShowEntry
      >(`/projects/${projectUuid}/run-of-show-entries/${entryUuid}/`, payload);

      return data.data ?? data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Schedule entry updated successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'run-of-show'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Unable to save schedule entry.'));
    },
  });
};

export const useDeleteRunOfShowEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectUuid, entryUuid }: { projectUuid: string; entryUuid: string }) => {
      await apiClient.delete(`/projects/${projectUuid}/run-of-show-entries/${entryUuid}/`);
      return { projectUuid, entryUuid };
    },
    onSuccess: ({ projectUuid }) => {
      toast.success('Schedule entry deleted');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'run-of-show'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Unable to delete schedule entry.'));
    },
  });
};
